import { Modal } from 'react-bootstrap'
import { hideModal } from '../../../store/actions/modalActions'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { UploadConfigDisclaimer } from './UploadConfigDisclaimer/UploadConfigDisclaimer'
import { UploadConfigHeader } from './UploadConfigHeader/UploadConfigHeader'
import { ChangeEvent, DragEvent, useEffect, useState } from 'react'
import { UploadFileDroppable } from './UploadFileDroppable/UploadFileDroppable'
import { UploadConfigFooter } from './UploadConfigFooter/UploadConfigFooter'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { selectOneProductConfig } from '../../../store/selectors/configSelectors'
import { selectModalData } from '../../../store/selectors/modalSelectors'
import { UploadConfigFileList } from './UploadConfigFileList/UploadConfigFileList'
import styles from './UploadConfigModal.module.scss'
import { uploadMultipleConfigs } from '../../../store/async-thunks/products/uploadMultipleConfigs'
import { FormattedMessage } from 'react-intl'

export const UploadMultipleConfigsModal = () => {
    const dispatch = useAppDispatch()
    const [isValid, setIsValid] = useState(false)
    const [error, setError] = useState('')
    const [dragging, setDragging] = useState(false)
    const [uploadedConfigs, setUploadedConfigs] = useState<File[]>([])
    const [selectedConfigNames, setSelectedConfigNames] = useState<
        Record<string, string>
    >({})

    const modalData = useAppSelector(selectModalData)
    const { guid } = modalData
    const configuration = useAppSelector(selectOneProductConfig(guid))
    const configNames = Object.keys(configuration)

    const handleClose = (): void => {
        dispatch(hideModal())
    }

    const handleConfigNameSelect = (idx: number, name: string) => {
        setSelectedConfigNames((prev) => ({ ...prev, [idx]: name }))
    }

    const handleFileDelete = (idx: number) => {
        setUploadedConfigs((prev) => {
            return prev.filter((_item, i) => i !== idx)
        })
        setSelectedConfigNames((prev) => {
            const copy = { ...prev }
            delete copy[idx]
            return copy
        })
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target?.files || [])
        if (files.length) {
            setSelectedConfigNames({})
            setUploadedConfigs(files)
        }
    }

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setDragging(true)
    }

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragging(false)
    }

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setDragging(false)
        const droppedFiles = Array.from(e.dataTransfer?.files || [])
        if (droppedFiles.length) {
            setUploadedConfigs(droppedFiles as unknown as File[])
            setSelectedConfigNames({})
        }
    }

    const handleSubmit = () => {
        const selectedConfigNamesEntries = Object.entries(selectedConfigNames)

        if (uploadedConfigs.length && selectedConfigNamesEntries.length) {
            const configKeyAndFileDict = selectedConfigNamesEntries.reduce(
                (
                    acc: Record<string, { config_key: string; file: File }>,
                    [index, configName]
                ) => {
                    acc[configName] = {
                        config_key: configName,
                        file: uploadedConfigs[Number(index)],
                    }
                    return acc
                },
                {}
            )
            const result = configNames
                .map((name) => configKeyAndFileDict[name])
                .filter(Boolean)

            const formData = new FormData()
            result.forEach((item, index) => {
                formData.append(`files[${index}].file`, item.file)
                formData.append(`files[${index}].config_key`, item.config_key)
            })
            dispatch(uploadMultipleConfigs({ guid, formData }))
        }

        setTimeout(() => handleClose(), 0)
    }

    useEffect(() => {
        function validate() {
            const selectedConfigNamesValues = Object.values(selectedConfigNames)
            if (configNames.length < uploadedConfigs.length) {
                setIsValid(false)
                setError('modals.configs.moreConfigFilesThanProductHas')
            } else if (
                uploadedConfigs.length !== selectedConfigNamesValues.length
            ) {
                setIsValid(false)
                setError('modals.configs.unselectedConfigNames')
            } else if (
                selectedConfigNamesValues.length !==
                new Set(selectedConfigNamesValues).size
            ) {
                setIsValid(false)
                setError('modals.configs.similarConfigNames')
            } else {
                setIsValid(true)
                setError('')
            }
        }

        validate()
    }, [uploadedConfigs, selectedConfigNames, configNames.length])

    return (
        <>
            <div className="bg-blur" />
            <Modal
                centered
                show={true}
                onHide={handleClose}
                dialogClassName={styles.customModal}
            >
                <UploadConfigHeader />
                <Modal.Body className={styles.windowPadding}>
                    <UploadConfigDisclaimer />
                    <UploadFileDroppable
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onChange={handleChange}
                        isDragging={dragging}
                        file={uploadedConfigs}
                        isMultiple
                    />
                    {error && (
                        <div className={`app-color-red pt-2 text-center ${styles.error}`}>
                            <FormattedMessage id={error} />
                        </div>
                    )}
                    <UploadConfigFileList
                        files={uploadedConfigs}
                        configNames={configNames}
                        onFileNameSelect={handleConfigNameSelect}
                        onFileDelete={handleFileDelete}
                        selectedConfigNames={selectedConfigNames}
                    />
                </Modal.Body>
                <UploadConfigFooter
                    onSubmit={handleSubmit}
                    isValid={isValid}
                />
            </Modal>
        </>
    )
}
